import React, { useEffect, useLayoutEffect, useState, useMemo, useRef, useCallback } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
// FontAwesome icons
import { FaEye, FaTrash, FaCloudUploadAlt, FaCloudDownloadAlt, FaMagic, FaPlus, FaShareAlt, FaCopy, FaNewspaper, FaCalendarAlt, FaFacebook } from "react-icons/fa";
import AgentBadge from "../components/AgentBadge";
import { useViewScope } from "../contexts/ViewScopeContext";

// Custom image blot that preserves width, height, and style attributes
const BlockEmbed = Quill.import("blots/block/embed");
class ImageBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    if (typeof value === "string") {
      node.setAttribute("src", value);
    } else {
      node.setAttribute("src", value.src || "");
      if (value.width) node.setAttribute("width", value.width);
      if (value.height) node.setAttribute("height", value.height);
      if (value.style) node.setAttribute("style", value.style);
      if (value.alt) node.setAttribute("alt", value.alt);
    }
    return node;
  }
  static value(node) {
    return {
      src: node.getAttribute("src"),
      width: node.getAttribute("width"),
      height: node.getAttribute("height"),
      style: node.getAttribute("style"),
      alt: node.getAttribute("alt")
    };
  }
}
ImageBlot.blotName = "image";
ImageBlot.tagName = "img";
Quill.register(ImageBlot, true);

export default function ManageArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState({});
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, slug: null });
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState("all"); // 'all', 'live', 'draft'
  const [editModal, setEditModal] = useState({ open: false, slug: null });
  const [editForm, setEditForm] = useState({ title: "", content: "", image_url: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [contentTab, setContentTab] = useState("edit"); // 'edit' or 'raw'
  // Cover-image upload state — separate per form so the spinner only shows
  // on the form being acted on. Refs are the hidden <input type="file">.
  const editUploadInputRef = useRef(null);
  const generateUploadInputRef = useRef(null);
  const [uploadingEditImage, setUploadingEditImage] = useState(false);
  const [uploadingGenerateImage, setUploadingGenerateImage] = useState(false);
  const [uploadImageError, setUploadImageError] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiEditing, setAiEditing] = useState(false);
  const [generateModal, setGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({ prompt: "", image_url: "", title: "", content: "", slug: null });
  const [generating, setGenerating] = useState(false);
  const [savingGenerate, setSavingGenerate] = useState(false);
  const [suburbQuery, setSuburbQuery] = useState("");
  const [suburbResults, setSuburbResults] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [suburbSearching, setSuburbSearching] = useState(false);
  const [suburbDropdownOpen, setSuburbDropdownOpen] = useState(false);
  // Agency scope (from central context)
  const { viewScope, agentFilter, mySlug } = useViewScope();
  // Separate suburb state for the edit modal
  const [editSuburbQuery, setEditSuburbQuery] = useState("");
  const [editSuburbResults, setEditSuburbResults] = useState([]);
  const [editSelectedLocations, setEditSelectedLocations] = useState([]);
  const [editSuburbSearching, setEditSuburbSearching] = useState(false);
  const [editSuburbDropdownOpen, setEditSuburbDropdownOpen] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "" });
  const [socialPostModal, setSocialPostModal] = useState({ open: false, slug: null });
  const [socialPostText, setSocialPostText] = useState("");
  const [socialPostLoading, setSocialPostLoading] = useState(false);
  const [socialPostPrompt, setSocialPostPrompt] = useState("");
  const [copyFlash, setCopyFlash] = useState(false);
  const socialPostArticleRef = useRef(null);
  const socialPostPromptRef = useRef(null);
  const socialPostTextRef = useRef(null);
  const [fbConnected, setFbConnected] = useState(false);
  const [fbPages, setFbPages] = useState([]);
  const [fbSelectedPage, setFbSelectedPage] = useState("");
  const [fbPosted, setFbPosted] = useState([]);
  const [fbPosting, setFbPosting] = useState(false);
  const [fbPostMsg, setFbPostMsg] = useState("");
  const [duplicating, setDuplicating] = useState({});
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [showScheduleFields, setShowScheduleFields] = useState(false);
  const [articleProgress, setArticleProgress] = useState(0);
  const articleProgressTimer = useRef(null);
  const streamingArticleRef = useRef(null);
  const streamingHtmlRef = useRef('');
  const defaultBlurbPrompt = `You are a social media copywriter for a real estate agent. Your job is to write a short, shareable social media blurb that entices people to click through to the full article.

Rules:
- The blurb should be 4-6 short lines/paragraphs max.
- Open with a punchy, attention-grabbing headline that references the location or key finding. Use an emoji.
- Follow with 1-2 sentences summarising the most interesting or surprising stat or trend from the article. Use real numbers from the article (percentages, dollar figures, changes).
- Then add a short punchy line — think three-word phrases separated by full stops, or a bold takeaway.
- End with a call-to-action line encouraging the reader to click the link to learn more. Use an emoji like 👇 or 🔗.
- After the CTA line, put the article URL on its own line
- Tone: confident, conversational, slightly urgent. Not salesy or formal.
- Do NOT use hashtags.
- Do NOT use em dashes.
- Return only the blurb text, no explanation or markdown formatting.`;
  // Auto-resize the social post prompt textarea to fit its content
  useEffect(() => {
    const el = socialPostPromptRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    }
  }, [socialPostPrompt]);

  // Auto-resize the social post blurb textarea to fit its content
  useLayoutEffect(() => {
    const el = socialPostTextRef.current;
    if (el) {
      el.style.height = "0px";
      el.style.height = Math.max(200, el.scrollHeight) + "px";
    }
  }, [socialPostText, socialPostLoading]);

  // Check Facebook connection status (only for bayan@vendorleads.com.au)
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const email = (user?.agent_details?.agent_email || "").toLowerCase();
      if (email !== "bayan@vendorleads.com.au") return;
    } catch { return; }
    handleTokenAndPostRequest(`${window.api_host}/facebook-status`)
      .then((res) => {
        if (res.data?.connected) {
          setFbConnected(true);
          setFbPages(res.data.pages || []);
          if (res.data.pages?.length) setFbSelectedPage(res.data.pages[0].page_id);
          setFbPosted(res.data.posted || []);
        }
      })
      .catch(() => {});
    handleTokenAndPostRequest(`${window.api_host}/scheduled-posts`)
      .then((res) => {
        if (res.data?.posts) setScheduledPosts(res.data.posts.filter(p => p.status === "pending"));
      })
      .catch(() => {});
  }, []);

  const suburbSearchTimer = useRef(null);
  const suburbInputRef = useRef(null);
  const editSuburbSearchTimer = useRef(null);
  const editSuburbInputRef = useRef(null);

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["link", "image"],
      ["clean"]
    ]
  }), []);

  const quillFormats = [
    "header", "bold", "italic", "underline", "strike",
    "color", "background", "list", "align",
    "blockquote", "code-block", "link", "image",
    "width", "height", "style"
  ];

  useEffect(() => {
    async function fetchArticles() {
      setLoading(true);
      setError(null);
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/articles-by-agency`,
          { view_scope: viewScope, agent_filter: agentFilter }
        );
        setArticles(Array.isArray(response.data) ? response.data : []);
      } catch (e) {
        setError("Failed to load articles.");
        setArticles([]);
      }
      setLoading(false);
    }
    fetchArticles();
  }, [viewScope, agentFilter]);

  async function handlePublish(slug, isLive) {
    setPublishing(prev => ({ ...prev, [slug]: true }));
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/edit-article`,
        { slug, is_live: isLive }
      );
      setArticles(articles =>
        articles.map(a =>
          a.slug === slug ? { ...a, is_live: isLive } : a
        )
      );
    } catch (e) {
      alert("Failed to update article.");
    }
    setPublishing(prev => ({ ...prev, [slug]: false }));
  }

  async function handleDelete(slug) {
    setDeleting(true);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/delete-article`,
        { slug }
      );
      setArticles(articles => articles.filter(a => a.slug !== slug));
      setDeleteModal({ open: false, slug: null });
    } catch (e) {
      alert("Failed to delete article.");
    }
    setDeleting(false);
  }

  async function handleDuplicate(slug) {
    setDuplicating(prev => ({ ...prev, [slug]: true }));
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/duplicate-article`,
        { slug }
      );
      if (res.data?.status === "success") {
        // Refresh articles list
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/articles-by-agency`,
          { view_scope: viewScope, agent_filter: agentFilter }
        );
        setArticles(Array.isArray(response.data) ? response.data : []);
        setToast({ show: true, message: "Article duplicated!" });
        setTimeout(() => setToast({ show: false, message: "" }), 3000);
      }
    } catch (e) {
      alert("Failed to duplicate article.");
    }
    setDuplicating(prev => ({ ...prev, [slug]: false }));
  }

  async function handleScheduleArticle() {
    if (!scheduleDate || !scheduleTime || !socialPostText.trim() || !fbSelectedPage) return;
    setScheduleSaving(true);
    try {
      const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}:00`).toISOString();
      const slug = socialPostModal.slug;
      // Check if there's an existing scheduled post for this article
      const existing = scheduledPosts.find(p => p.asset_type === "article" && p.asset_id === slug);
      if (existing && existing.id) {
        // Update existing scheduled post
        await handleTokenAndPostRequest(
          `${window.api_host}/update-scheduled-post`,
          { id: existing.id, message: socialPostText, scheduled_at: scheduledAt, page_id: fbSelectedPage }
        );
        setScheduledPosts(prev => prev.map(p =>
          p.id === existing.id ? { ...p, message: socialPostText, scheduled_at: scheduledAt, page_id: fbSelectedPage } : p
        ));
        setToast({ show: true, message: "Schedule updated!" });
      } else {
        // Create new scheduled post
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/schedule-social-post`,
          { asset_type: "article", asset_id: slug, page_id: fbSelectedPage, message: socialPostText, scheduled_at: scheduledAt }
        );
        setScheduledPosts(prev => [...prev, { id: res.data?.id, asset_type: "article", asset_id: slug, scheduled_at: scheduledAt, status: "pending", message: socialPostText, page_id: fbSelectedPage }]);
        setToast({ show: true, message: "Post scheduled!" });
      }
      setSocialPostModal({ open: false, slug: null });
      setShowScheduleFields(false);
      setScheduleDate("");
      setScheduleTime("09:00");
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
    } catch (e) {
      alert("Failed to schedule post.");
    }
    setScheduleSaving(false);
  }

  // Upload an image file to S3 via /upload-images, then set the returned URL
  // onto whichever form (edit or generate) the user is on. The endpoint already
  // handles auth via the JWT in handleTokenAndPostRequest.
  async function uploadCoverImage(file, form) {
    if (!file) return;
    setUploadImageError("");
    const setUploading = form === "edit" ? setUploadingEditImage : setUploadingGenerateImage;
    const setForm = form === "edit" ? setEditForm : setGenerateForm;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("files", file);
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/upload-images`,
        formData,
      );
      const url = res?.data?.uploaded_urls?.[0];
      if (!url) {
        throw new Error("Upload failed: no URL returned");
      }
      setForm(f => ({ ...f, image_url: url }));
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Upload failed";
      setUploadImageError(msg);
    } finally {
      setUploading(false);
    }
  }

  function openEditModal(article) {
    setEditForm({ title: article.title || "", content: article.content || "", image_url: article.cover_image || "" });
    setEditModal({ open: true, slug: article.slug });
    setContentTab("edit");
    setAiPrompt("");
    // Pre-populate suburb selections from existing article data
    const existingLocations = (article.suburb_ids || []).map(id => ({
      value: id,
      label: id,
      type: "suburb"
    }));
    setEditSelectedLocations(existingLocations);
    setEditSuburbQuery("");
    setEditSuburbResults([]);
    setEditSuburbDropdownOpen(false);
  }

  function closeEditModal() {
    if (savingEdit || aiEditing) return;
    setEditModal({ open: false, slug: null });
    setEditSelectedLocations([]);
    setEditSuburbQuery("");
  }

  async function handleAiEdit() {
    const currentContent = editModal.open ? editForm.content : generateForm.content;
    if (!aiPrompt.trim() || !currentContent) return;
    setAiEditing(true);
    // Start progress bar
    setArticleProgress(0);
    const startTime = Date.now();
    const DURATION = 60000;
    if (articleProgressTimer.current) clearInterval(articleProgressTimer.current);
    articleProgressTimer.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const ratio = Math.min(elapsed / DURATION, 1);
      const progress = Math.min(95, ratio * 100 * (2 - ratio));
      setArticleProgress(progress);
    }, 200);
    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/article-ai-edit`,
        { content: currentContent, prompt: aiPrompt }
      );
      if (response.data && response.data.content) {
        if (editModal.open) {
          setEditForm(f => ({ ...f, content: response.data.content }));
        } else {
          setGenerateForm(f => ({ ...f, content: response.data.content }));
        }
        setAiPrompt("");
        setToast({ show: true, message: "AI edit applied — review the changes and save when you're happy." });
        setTimeout(() => setToast({ show: false, message: "" }), 4000);
      } else {
        alert("AI edit did not return updated content.");
      }
    } catch (e) {
      alert("Failed to apply AI edit.");
    }
    if (articleProgressTimer.current) clearInterval(articleProgressTimer.current);
    setArticleProgress(100);
    setTimeout(() => setArticleProgress(0), 500);
    setAiEditing(false);
  }

  // Post-process Quill HTML so it renders properly outside the editor
  function sanitizeContentForSave(html) {
    // Replace non-breaking spaces with regular spaces (Quill converts all spaces to &nbsp;)
    let cleaned = html
      .replace(/&nbsp;/gi, " ")
      .replace(/\u00A0/g, " ")
      // Remove empty paragraphs Quill inserts (<p><br></p>, <p></p>)
      .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, "")
      .replace(/<p>\s*<\/p>/gi, "")
      // Remove trailing <br> inside block elements
      .replace(/<br\s*\/?>\s*(<\/(p|div|h[1-6]|li|blockquote)>)/gi, "$1")
      // Collapse multiple <br> into one
      .replace(/(<br\s*\/?>){2,}/gi, "<br>");

    return cleaned;
  }

  const searchSuburbs = useCallback((query) => {
    if (suburbSearchTimer.current) clearTimeout(suburbSearchTimer.current);
    if (!query.trim()) {
      setSuburbResults([]);
      setSuburbDropdownOpen(false);
      return;
    }
    suburbSearchTimer.current = setTimeout(async () => {
      setSuburbSearching(true);
      try {
        const res = await fetch(
          `${window.api_host}/location-search?q=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        // Filter out already-selected items
        const filtered = (Array.isArray(data) ? data : []).filter(
          item => !selectedLocations.some(s => s.value === item.value && s.type === item.type)
        );
        setSuburbResults(filtered);
        setSuburbDropdownOpen(filtered.length > 0);
      } catch (e) {
        setSuburbResults([]);
      }
      setSuburbSearching(false);
    }, 300);
  }, [selectedLocations]);

  function formatLocationLabel(item) {
    if (item.type === "postcode") {
      return `Postcode ${item.value}${item.state ? `, ${item.state}` : ""}`;
    }
    return item.label || item.value;
  }

  function handleSelectLocation(item) {
    if (selectedLocations.some(s => s.value === item.value && s.type === item.type)) return;
    setSelectedLocations(prev => [...prev, item]);
    setSuburbQuery("");
    // Keep dropdown open with remaining results (remove the just-selected item)
    const remaining = suburbResults.filter(r => !(r.value === item.value && r.type === item.type));
    setSuburbResults(remaining);
    if (remaining.length === 0) {
      setSuburbDropdownOpen(false);
    }
    if (suburbInputRef.current) suburbInputRef.current.focus();
  }

  function handleRemoveLocation(idx) {
    setSelectedLocations(prev => prev.filter((_, i) => i !== idx));
  }

  // --- Edit-modal suburb search (separate from generate-modal) ---
  const editSearchSuburbs = useCallback((query) => {
    if (editSuburbSearchTimer.current) clearTimeout(editSuburbSearchTimer.current);
    if (!query.trim()) {
      setEditSuburbResults([]);
      setEditSuburbDropdownOpen(false);
      return;
    }
    editSuburbSearchTimer.current = setTimeout(async () => {
      setEditSuburbSearching(true);
      try {
        const res = await fetch(
          `${window.api_host}/location-search?q=${encodeURIComponent(query)}`
        );
        const data = await res.json();
        const filtered = (Array.isArray(data) ? data : []).filter(
          item => !editSelectedLocations.some(s => s.value === item.value && s.type === item.type)
        );
        setEditSuburbResults(filtered);
        setEditSuburbDropdownOpen(filtered.length > 0);
      } catch (e) {
        setEditSuburbResults([]);
      }
      setEditSuburbSearching(false);
    }, 300);
  }, [editSelectedLocations]);

  function handleEditSelectLocation(item) {
    if (editSelectedLocations.some(s => s.value === item.value && s.type === item.type)) return;
    setEditSelectedLocations(prev => [...prev, item]);
    setEditSuburbQuery("");
    const remaining = editSuburbResults.filter(r => !(r.value === item.value && r.type === item.type));
    setEditSuburbResults(remaining);
    if (remaining.length === 0) {
      setEditSuburbDropdownOpen(false);
    }
    if (editSuburbInputRef.current) editSuburbInputRef.current.focus();
  }

  function handleEditRemoveLocation(idx) {
    setEditSelectedLocations(prev => prev.filter((_, i) => i !== idx));
  }

  async function refetchArticles() {
    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/articles-by-agency`,
        {}
      );
      setArticles(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      // silently fail on refetch
    }
  }

  async function handleGenerate() {
    if (!generateForm.prompt.trim()) return;
    setGenerating(true);
    setContentTab("edit");
    setGenerateForm(f => ({ ...f, content: "", title: "", slug: "" }));
    streamingHtmlRef.current = '';
    if (streamingArticleRef.current) streamingArticleRef.current.innerHTML = '';
    // Start progress bar
    setArticleProgress(0);
    const startTime = Date.now();
    const DURATION = 60000;
    if (articleProgressTimer.current) clearInterval(articleProgressTimer.current);
    articleProgressTimer.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const ratio = Math.min(elapsed / DURATION, 1);
      const progress = Math.min(95, ratio * 100 * (2 - ratio));
      setArticleProgress(progress);
    }, 200);
    try {
      const suburbIds = selectedLocations.map(loc => loc.value);
      const jwt_token = localStorage.getItem("jwt_token");
      const headers = { 'Content-Type': 'application/json' };
      if (jwt_token) headers['Authorization'] = `Bearer ${jwt_token}`;

      const resp = await fetch(`${window.api_host}/agent-generate-article`, {
        method: 'POST', headers,
        body: JSON.stringify({ prompt: generateForm.prompt, image_url: generateForm.image_url, suburb_ids: suburbIds, stream: true }),
      });

      if (!resp.ok) {
        alert("Failed to generate article. Please try again.");
      } else {
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const events = buf.split('\n\n');
          buf = events.pop();
          for (const event of events) {
            const dataLine = event.split('\n').find(l => l.startsWith('data: '));
            if (!dataLine) continue;
            try {
              const parsed = JSON.parse(dataLine.slice(6));
              if (parsed.type === 'chunk') {
                streamingHtmlRef.current += parsed.content;
                if (streamingArticleRef.current) {
                  const frag = document.createRange().createContextualFragment(parsed.content);
                  streamingArticleRef.current.appendChild(frag);
                  streamingArticleRef.current.scrollTop = streamingArticleRef.current.scrollHeight;
                }
              } else if (parsed.type === 'done') {
                setGenerateForm(f => ({
                  ...f,
                  title: parsed.title || "",
                  content: parsed.content || streamingHtmlRef.current,
                  slug: parsed.slug || "",
                }));
                await refetchArticles();
                setToast({ show: true, message: "Article generated successfully!" });
                setTimeout(() => setToast({ show: false, message: "" }), 3000);
              } else if (parsed.type === 'error') {
                alert("Failed to generate article.");
              }
            } catch {}
          }
        }
      }
    } catch (e) {
      alert("Failed to generate article.");
    }
    if (articleProgressTimer.current) clearInterval(articleProgressTimer.current);
    setArticleProgress(100);
    setTimeout(() => setArticleProgress(0), 500);
    setGenerating(false);
  }

  async function handleGenerateSave() {
    if (!generateForm.slug) return;
    setSavingGenerate(true);
    const processedContent = sanitizeContentForSave(generateForm.content);
    const suburbIds = selectedLocations.map(loc => loc.value);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/edit-article`,
        {
          slug: generateForm.slug,
          title: generateForm.title,
          content: processedContent,
          tiny_image: generateForm.image_url,
          cover_image: generateForm.image_url,
          suburb_ids: suburbIds
        }
      );
      setArticles(articles =>
        articles.map(a =>
          a.slug === generateForm.slug
            ? { ...a, title: generateForm.title, content: processedContent, cover_image: generateForm.image_url, tiny_image: generateForm.image_url, suburb_ids: suburbIds }
            : a
        )
      );
      setToast({ show: true, message: "Changes saved successfully!" });
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
    } catch (e) {
      alert("Failed to save article.");
    }
    setSavingGenerate(false);
  }

  function closeGenerateModal() {
    if (generating || savingGenerate) return;
    setGenerateModal(false);
    setGenerateForm({ prompt: "", image_url: "", title: "", content: "", slug: null });
    setSelectedLocations([]);
    setSuburbQuery("");
  }

  async function handleEditSubmit() {
    if (!editModal.slug) return;
    setSavingEdit(true);
    const processedContent = sanitizeContentForSave(editForm.content);
    const suburbIds = editSelectedLocations.map(loc => loc.value);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/edit-article`,
        {
          slug: editModal.slug,
          title: editForm.title,
          content: processedContent,
          tiny_image: editForm.image_url,
          cover_image: editForm.image_url,
          suburb_ids: suburbIds
        }
      );
      setArticles(articles =>
        articles.map(a =>
          a.slug === editModal.slug
            ? { ...a, title: editForm.title, content: processedContent, cover_image: editForm.image_url, tiny_image: editForm.image_url, suburb_ids: suburbIds }
            : a
        )
      );
      setToast({ show: true, message: "Changes saved successfully!" });
      setTimeout(() => setToast({ show: false, message: "" }), 3000);
    } catch (e) {
      alert("Failed to update article.");
    }
    setSavingEdit(false);
  }

  async function handleSocialPost(article, existingScheduled) {
    socialPostArticleRef.current = article;
    setSocialPostModal({ open: true, slug: article.slug });
    setSocialPostText("");
    setSocialPostPrompt(defaultBlurbPrompt);
    setSocialPostLoading(true);
    setFbPostMsg("");
    if (existingScheduled) {
      setShowScheduleFields(true);
      const dt = new Date(existingScheduled.scheduled_at);
      setScheduleDate(dt.toISOString().split('T')[0]);
      setScheduleTime(dt.toTimeString().slice(0, 5));
    } else {
      setShowScheduleFields(false);
      setScheduleDate("");
      setScheduleTime("09:00");
    }
    if (existingScheduled) {
      setSocialPostText(existingScheduled.message || "");
      setSocialPostLoading(false);
      return;
    }
    const articleUrl = article.article_url || `https://www.homeprice.site/news/${article.slug}${article.landing_page_slug ? `?agent=${article.landing_page_slug}` : ""}`;
    try {
      const jwt_token = localStorage.getItem("jwt_token");
      const headers = { 'Content-Type': 'application/json' };
      if (jwt_token) headers['Authorization'] = `Bearer ${jwt_token}`;
      const resp = await fetch(`${window.api_host}/article-ai-blurb`, {
        method: 'POST', headers,
        body: JSON.stringify({ title: article.title, body: article.content, blurb_prompt: defaultBlurbPrompt, article_url: articleUrl, stream: true }),
      });
      if (!resp.ok) { setSocialPostText("Failed to generate social post."); setSocialPostLoading(false); return; }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '', accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop();
        for (const event of events) {
          const dataLine = event.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(6));
            if (parsed.type === 'chunk') { accumulated += parsed.content; setSocialPostText(accumulated); }
          } catch {}
        }
      }
      if (!accumulated) setSocialPostText("Failed to generate social post.");
    } catch (e) {
      setSocialPostText("Failed to generate social post.");
    }
    setSocialPostLoading(false);
  }

  async function handleRegenerateBlurb() {
    const article = socialPostArticleRef.current;
    if (!article) return;
    setSocialPostText("");
    setSocialPostLoading(true);
    const articleUrl = article.article_url || `https://www.homeprice.site/news/${article.slug}${article.landing_page_slug ? `?agent=${article.landing_page_slug}` : ""}`;
    try {
      const jwt_token = localStorage.getItem("jwt_token");
      const headers = { 'Content-Type': 'application/json' };
      if (jwt_token) headers['Authorization'] = `Bearer ${jwt_token}`;
      const resp = await fetch(`${window.api_host}/article-ai-blurb`, {
        method: 'POST', headers,
        body: JSON.stringify({ title: article.title, body: article.content, blurb_prompt: socialPostPrompt, article_url: articleUrl, stream: true }),
      });
      if (!resp.ok) { setSocialPostText("Failed to generate social post."); setSocialPostLoading(false); return; }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '', accumulated = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split('\n\n');
        buf = events.pop();
        for (const event of events) {
          const dataLine = event.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            const parsed = JSON.parse(dataLine.slice(6));
            if (parsed.type === 'chunk') { accumulated += parsed.content; setSocialPostText(accumulated); }
          } catch {}
        }
      }
      if (!accumulated) setSocialPostText("Failed to generate social post.");
    } catch {
      setSocialPostText("Failed to generate social post.");
    }
    setSocialPostLoading(false);
  }

  // Responsive padding for mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // FE filter logic
  const filteredArticles = articles
    .filter(a =>
      filter === "all" ? true : filter === "live" ? a.is_live : !a.is_live
    )
    .sort((a, b) => {
      const dateA = a.publish_date ? new Date(a.publish_date) : new Date(0);
      const dateB = b.publish_date ? new Date(b.publish_date) : new Date(0);
      return dateB - dateA;
    });

  return (
    <div style={{
      padding: isMobile ? "0 16px 0 16px" : "0 32px 0 32px",
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      background: "#f6f8fa",
      boxSizing: "border-box",
      width: "100%",
      position: "relative",
      overflow: "auto"
    }}>
    <div style={{ maxWidth: 900, marginLeft: "auto", marginRight: "auto", width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 10, background: "#f6f8fa", paddingTop: isMobile ? 72 : 32, marginLeft: isMobile ? -16 : -32, marginRight: isMobile ? -16 : -32, paddingLeft: isMobile ? 16 : 32, paddingRight: isMobile ? 16 : 32 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <h1 style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: "#1f2937", margin: 0 }}>Manage Articles</h1>
          </div>
          <button
            onClick={() => { setGenerateModal(true); setContentTab("edit"); setAiPrompt(""); }}
            style={{
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 20px",
              fontWeight: 700,
              cursor: "pointer",
              fontSize: 15,
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 1px 4px #0001"
            }}
          >
            <FaPlus /> Generate Article
          </button>
        </div>
        {/* Filter tabs */}
        <div
          style={{
            display: "flex",
            gap: 4,
            padding: 4,
            background: "#eef2f7",
            borderRadius: 10,
            marginBottom: 24,
            width: "fit-content",
            maxWidth: "100%",
            overflowX: "auto",
          }}
        >
          {[
            { id: "all", label: "All" },
            { id: "live", label: "Live" },
            { id: "draft", label: "Draft" },
          ].map((t) => {
            const active = filter === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                style={{
                  background: active ? "#fff" : "transparent",
                  color: active ? "#111827" : "#4b5563",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 16px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  whiteSpace: "nowrap",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 16, overflow: "hidden",
              boxShadow: "0 1px 4px #0001", border: "1px solid #e5e7eb",
              display: "flex", flexDirection: isMobile ? "column" : "row",
            }}>
              <div style={{ width: isMobile ? "100%" : 200, minHeight: isMobile ? 160 : 140, position: "relative" }}>
                <Skeleton height="100%" width="100%" borderRadius={0} style={{ display: "block", minHeight: isMobile ? 160 : 140 }} />
              </div>
              <div style={{ flex: 1, padding: "18px 22px", display: "flex", flexDirection: "column", gap: 8 }}>
                <Skeleton width="70%" height={16} />
                <Skeleton width="30%" height={12} />
                <div style={{ display: "flex", gap: 6 }}>
                  <Skeleton width={60} height={18} borderRadius={10} />
                  <Skeleton width={70} height={18} borderRadius={10} />
                </div>
                <Skeleton width="90%" height={12} />
                <Skeleton width="60%" height={12} />
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <Skeleton width={60} height={28} borderRadius={8} />
                  <Skeleton width={70} height={28} borderRadius={8} />
                  <Skeleton width={60} height={28} borderRadius={8} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div style={{ color: "red", marginBottom: 24 }}>{error}</div>
      ) : filteredArticles.length === 0 ? (
        articles.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{
            background: "#f8fafc",
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 2px 8px #0001",
            marginBottom: 24,
            border: "1px solid #e5e7eb"
          }}>
            <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12, color: "#1f2937" }}>No Articles Yet</h2>
            <p style={{ fontSize: 16, color: "#374151", marginBottom: 16 }}>
              No articles found for your agency. Soon you’ll be able to generate articles that act as lead capture forms, using our property data and insights we collect about what’s happening in your area.
            </p>
          </div>
          </div>
        ) : filter === "draft" ? (
          <div style={{
            background: "#fff7ed",
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 2px 8px #0001",
            marginBottom: 24,
            border: "1px solid #f59e42"
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "#f59e42" }}>No Draft Articles</h2>
            <p style={{ fontSize: 16, color: "#b45309", marginBottom: 0 }}>
              All your articles are live. Switch to "All" or "Live" to view them.
            </p>
          </div>
        ) : filter === "live" ? (
          <div style={{
            background: "#e7fbe9",
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 2px 8px #0001",
            marginBottom: 24,
            border: "1px solid #22c55e"
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "#22c55e" }}>No Live Articles</h2>
            <p style={{ fontSize: 16, color: "#15803d", marginBottom: 0 }}>
              All your articles are in draft mode. Switch to "All" or "Draft" to view them.
            </p>
          </div>
        ) : null
      ) : (
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 16
        }}>
          {filteredArticles.map(article => (
            <div
              key={article.slug}
              style={{
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                border: "1px solid #e5e7eb",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                overflow: "hidden",
                transition: "box-shadow 0.2s, transform 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.12)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
            >
              {/* Cover image / thumbnail */}
              <div style={{
                position: "relative",
                flexShrink: 0,
                width: isMobile ? "100%" : 200,
                height: isMobile ? 180 : "auto",
                minHeight: isMobile ? undefined : 160,
                background: "#f3f4f6",
              }}>
                {article.cover_image ? (
                  <img
                    src={article.cover_image}
                    alt="cover"
                    referrerPolicy="no-referrer"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", minHeight: isMobile ? 180 : 160 }}>
                    <FaNewspaper style={{ fontSize: 36, color: "#d1d5db" }} />
                  </div>
                )}
                {/* Status badge overlay */}
                <div style={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  borderRadius: 20,
                  padding: "3px 12px",
                  fontWeight: 700,
                  fontSize: 11,
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  background: article.is_live ? "rgba(34,197,94,0.85)" : "rgba(0,0,0,0.55)",
                  color: "#fff",
                  letterSpacing: 0.3,
                }}>
                  {article.is_live ? "Live" : "Draft"}
                </div>
              </div>
              {/* Card body */}
              <div style={{
                padding: isMobile ? "16px 16px 14px" : "18px 22px",
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0,
                gap: 8,
              }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#1a1a1a", lineHeight: 1.3 }}>
                {article.title}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <AgentBadge agentName={article.agent_name} />
              </div>
              <div style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                {article.description}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {article.author_image && (
                  <img
                    src={article.author_image}
                    alt="author"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      objectFit: "cover"
                    }}
                  />
                )}
                <span style={{ fontWeight: 500, fontSize: 13, color: "#374151" }}>{article.author}</span>
                {article.author_title && (
                  <span style={{ color: "#9ca3af", fontSize: 12 }}>
                    {article.author_title}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 3, background: "#f3f4f6", color: "#374151", padding: "2px 10px", borderRadius: 10, fontWeight: 500, fontSize: 11 }}>
                  <FaCalendarAlt style={{ fontSize: 10 }} />
                  {article.publish_date ? new Date(article.publish_date).toLocaleDateString() : "—"}
                </span>
              </div>
              {/* Button group */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 4,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <a
                  href={
                    article.article_url ||
                    (`https://www.homeprice.site/news/${article.slug}?agent=${article.landing_page_slug}`)
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 12,
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <FaEye style={{ fontSize: 11 }} />
                  View
                </a>
                {!article.is_live ? (
                  <button
                    onClick={() => handlePublish(article.slug, true)}
                    disabled={publishing[article.slug]}
                    style={{
                      background: "#7c3aed",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 14px",
                      fontWeight: 600,
                      cursor: publishing[article.slug] ? "not-allowed" : "pointer",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {publishing[article.slug]
                      ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                      : <FaCloudUploadAlt style={{ fontSize: 11 }} />}
                    {publishing[article.slug] ? "Publishing..." : "Publish"}
                  </button>
                ) : (
                  <button
                    onClick={() => handlePublish(article.slug, false)}
                    disabled={publishing[article.slug]}
                    style={{
                      background: "#f3f4f6",
                      color: "#374151",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 14px",
                      fontWeight: 600,
                      cursor: publishing[article.slug] ? "not-allowed" : "pointer",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {publishing[article.slug]
                      ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                      : <FaCloudDownloadAlt style={{ fontSize: 11 }} />}
                    {publishing[article.slug] ? "Unpublishing..." : "Unpublish"}
                  </button>
                )}
                <button
                  onClick={() => openEditModal(article)}
                  style={{
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 12,
                    display: (viewScope === "agency" && article.landing_page_slug !== mySlug) ? "none" : "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  Edit
                </button>
                {(() => {
                  const posted = fbPosted.some(p => p.asset_type === "article" && p.asset_id === article.slug);
                  const scheduled = scheduledPosts.find(p => p.asset_type === "article" && p.asset_id === article.slug);
                  return (
                    <>
                      <button
                        onClick={() => !posted && handleSocialPost(article, scheduled || null)}
                        disabled={posted}
                        style={{
                          background: posted ? "#d1d5db" : scheduled ? "#f5f3ff" : "#f3f4f6",
                          color: posted ? "#6b7280" : scheduled ? "#6d28d9" : "#374151",
                          border: scheduled && !posted ? "1px solid #ddd6fe" : "none",
                          borderRadius: 8,
                          padding: "6px 14px",
                          fontWeight: 600,
                          cursor: posted ? "not-allowed" : "pointer",
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <FaShareAlt style={{ fontSize: 11 }} />
                        {posted ? "Already Posted" : scheduled ? "Edit Schedule" : "Social Post"}
                      </button>
                      {scheduled && (
                        <span style={{
                          fontSize: 11,
                          color: "#7c3aed",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}>
                          <FaCalendarAlt style={{ fontSize: 10 }} />
                          {new Date(scheduled.scheduled_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                          {" "}
                          {new Date(scheduled.scheduled_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </>
                  );
                })()}
                {(viewScope !== "agency" || article.landing_page_slug === mySlug) && (
                  <button
                    onClick={() => handleDuplicate(article.slug)}
                    disabled={duplicating[article.slug]}
                    style={{
                      background: "#f3f4f6",
                      color: "#374151",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 14px",
                      fontWeight: 600,
                      cursor: duplicating[article.slug] ? "not-allowed" : "pointer",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {duplicating[article.slug]
                      ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                      : <FaCopy style={{ fontSize: 11 }} />}
                    {duplicating[article.slug] ? "Duplicating..." : "Duplicate"}
                  </button>
                )}
                <button
                  onClick={() => setDeleteModal({ open: true, slug: article.slug })}
                  style={{
                    background: "transparent",
                    color: "#9ca3af",
                    border: "none",
                    borderRadius: 8,
                    padding: "6px 10px",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 12,
                    display: (viewScope === "agency" && article.landing_page_slug !== mySlug) ? "none" : "flex",
                    alignItems: "center",
                    gap: 5,
                    marginLeft: "auto",
                  }}
                >
                  {deleting && deleteModal.slug === article.slug
                    ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                    : <FaTrash style={{ fontSize: 11 }} />}
                </button>
              </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ color: "#888", fontSize: 15, marginTop: 32, marginBottom: 40 }}>
        Want to see a specific article type? Let us know!
      </div>
      </div>
      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div style={{
          position: "fixed",
          top: 0, right: 0, bottom: 0,
          left: isMobile ? 0 : 260,
          background: "rgba(0,0,0,0.25)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <div style={{
            background: "#fff",
            borderRadius: 10,
            padding: 32,
            minWidth: 320,
            maxWidth: 400,
            width: "90vw",
            boxShadow: "0 4px 24px #0002",
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Delete Article?</div>
            <div style={{ color: "#444", marginBottom: 24, textAlign: "center" }}>
              Are you sure you want to delete this article? This action cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              <button
                onClick={() => setDeleteModal({ open: false, slug: null })}
                disabled={deleting}
                style={{
                  background: "#f1f5f9",
                  color: "#374151",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteModal.slug)}
                disabled={deleting}
                style={{
                  background: "#e11d48",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {!deleting && <FaTrash style={{ marginRight: 8 }} />}
                {deleting && <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />}
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      {editModal.open && (
        <div
          onClick={closeEditModal}
          style={{
            position: "fixed",
            top: 0, right: 0, bottom: 0,
            left: isMobile ? 0 : 260,
            background: "rgba(0,0,0,0.25)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 0,
              width: "100%",
              height: "100%",
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, padding: "20px 28px 12px 28px", borderBottom: "1px solid #e5e7eb", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              Edit Article
              <button
                onClick={closeEditModal}
                disabled={savingEdit || aiEditing}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#9ca3af", padding: "0 4px", lineHeight: 1 }}
                title="Close"
              >×</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Title</label>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>The headline displayed at the top of your article and in search results.</div>
              <input
                value={editForm.title}
                onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Title"
                style={{
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: "1px solid #e5e7eb",
                  fontSize: 16,
                  width: "100%",
                  boxSizing: "border-box"
                }}
              />
            </div>
            <div>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Cover Image</label>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Paste a URL or upload a file. This appears at the top of your article and as the thumbnail in listings.</div>
              <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
                <input
                  value={editForm.image_url}
                  onChange={e => setEditForm(f => ({ ...f, image_url: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                  disabled={uploadingEditImage}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 16,
                    flex: 1,
                    minWidth: 0,
                    boxSizing: "border-box"
                  }}
                />
                <button
                  type="button"
                  onClick={() => editUploadInputRef.current?.click()}
                  disabled={uploadingEditImage}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 6,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#374151",
                    cursor: uploadingEditImage ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {uploadingEditImage ? "Uploading…" : "Upload"}
                </button>
                <input
                  ref={editUploadInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={e => {
                    if (e.target.files?.[0]) uploadCoverImage(e.target.files[0], "edit");
                    e.target.value = "";
                  }}
                />
              </div>
              {uploadImageError && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#dc2626" }}>
                  {uploadImageError}
                </div>
              )}
              {editForm.image_url && (
                <img
                  src={editForm.image_url}
                  alt="preview"
                  referrerPolicy="no-referrer"
                  style={{ marginTop: 8, maxHeight: 120, borderRadius: 6, objectFit: "cover" }}
                  onError={e => { e.target.style.display = "none"; }}
                  onLoad={e => { e.target.style.display = "block"; }}
                />
              )}
            </div>
            {/* Suburb / Postcode multi-select typeahead */}
            <div style={{ position: "relative" }}>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Suburbs / Postcodes</label>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Choose one or more suburbs or postcodes to target. This helps match the article to local readers and adds market data context.</div>
              {editSelectedLocations.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {editSelectedLocations.map((loc, idx) => (
                    <span
                      key={`${loc.type}-${loc.value}-${idx}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        borderRadius: 16,
                        padding: "3px 10px",
                        fontSize: 13,
                        fontWeight: 600
                      }}
                    >
                      {formatLocationLabel(loc)}
                      <button
                        onClick={() => handleEditRemoveLocation(idx)}
                        disabled={savingEdit}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#1d4ed8",
                          cursor: "pointer",
                          padding: 0,
                          fontSize: 15,
                          lineHeight: 1,
                          fontWeight: 700,
                          marginLeft: 2
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ position: "relative" }}>
                <input
                  ref={editSuburbInputRef}
                  value={editSuburbQuery}
                  autoComplete="off"
                  name="suburb-search-edit"
                  onChange={e => { setEditSuburbQuery(e.target.value); editSearchSuburbs(e.target.value); }}
                  onFocus={() => { if (editSuburbResults.length > 0) setEditSuburbDropdownOpen(true); }}
                  onBlur={() => { setTimeout(() => { setEditSuburbDropdownOpen(false); setEditSuburbQuery(""); }, 200); }}
                  placeholder="Type a suburb or postcode…"
                  disabled={savingEdit}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 15,
                    boxSizing: "border-box"
                  }}
                />
                {editSuburbSearching && (
                  <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
                    <Spinner animation="border" size="sm" style={{ width: 16, height: 16 }} />
                  </div>
                )}
              </div>
              {editSuburbDropdownOpen && editSuburbResults.length > 0 && (
                <div style={{
                  position: "absolute",
                  left: 0, right: 0,
                  top: "100%",
                  zIndex: 10,
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  boxShadow: "0 8px 24px #0002",
                  maxHeight: 220,
                  overflowY: "auto",
                  marginTop: 4
                }}>
                  {editSuburbResults.map((item, idx) => (
                    <div
                      key={`${item.type}-${item.value}-${idx}`}
                      onMouseDown={e => { e.preventDefault(); handleEditSelectLocation(item); }}
                      style={{
                        padding: "10px 14px",
                        cursor: "pointer",
                        fontSize: 14,
                        borderBottom: idx < editSuburbResults.length - 1 ? "1px solid #f3f4f6" : "none",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                      onMouseOver={e => e.currentTarget.style.background = "#f0f7ff"}
                      onMouseOut={e => e.currentTarget.style.background = "#fff"}
                    >
                      <span>{formatLocationLabel(item)}</span>
                      <span style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", fontWeight: 600 }}>{item.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* AI Edit Prompt */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>AI Edit Prompt</label>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Describe changes you'd like the AI to make to your article. You can also paste source text or data for the AI to reference.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {[
                  { label: "✍️ More Professional", prompt: "Make the tone more professional and authoritative while keeping the content accessible." },
                  { label: "📝 Add CTA", prompt: "Add a strong call-to-action at the end encouraging readers to contact the agent for a free property appraisal." },
                  { label: "✂️ Make Shorter", prompt: "Condense the article to be more concise while keeping the key statistics and insights." },
                  { label: "🎯 SEO Optimise", prompt: "Optimise the article for SEO by improving headings, adding relevant keywords naturally, and ensuring good structure." },
                  { label: "💡 Add Statistics", prompt: "Enhance the article with more specific statistics, data points, and market comparisons." },
                ].map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setAiPrompt(example.prompt)}
                    disabled={aiEditing}
                    style={{
                      background: aiPrompt === example.prompt ? "#dbeafe" : "#f3f4f6",
                      color: aiPrompt === example.prompt ? "#1d4ed8" : "#374151",
                      border: aiPrompt === example.prompt ? "1px solid #93c5fd" : "1px solid #e5e7eb",
                      borderRadius: 999,
                      padding: "5px 14px",
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: aiEditing ? "not-allowed" : "pointer",
                      whiteSpace: "nowrap",
                      transition: "all 0.15s",
                    }}
                  >
                    {example.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexDirection: isMobile ? "column" : "row" }}>
                <textarea
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  placeholder="Describe how to edit the article. You can also paste source text or data here for the AI to reference.
e.g. Make the tone more professional, add a call-to-action…"
                  disabled={aiEditing}
                  rows={3}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 15,
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "inherit",
                    width: isMobile ? "100%" : undefined
                  }}
                />
                <button
                  onClick={handleAiEdit}
                  disabled={aiEditing || !aiPrompt.trim()}
                  style={{
                    background: aiEditing ? "#a78bfa" : "#7c3aed",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "8px 18px",
                    fontWeight: 600,
                    cursor: aiEditing || !aiPrompt.trim() ? "not-allowed" : "pointer",
                    fontSize: 15,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                    minWidth: 100,
                    opacity: !aiPrompt.trim() ? 0.5 : 1,
                    width: isMobile ? "100%" : undefined
                  }}
                >
                  {aiEditing
                    ? <Spinner animation="border" size="sm" style={{ marginRight: 8, width: 14, height: 14 }} />
                    : <FaMagic style={{ marginRight: 8 }} />}
                  Apply
                </button>
              </div>
            </div>
            {/* Content tabs: Edit / Raw HTML */}
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Content</label>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Use the rich-text editor to format your article, or switch to Raw HTML for direct code editing.</div>
              <div style={{
                display: "flex",
                gap: 0,
                marginBottom: 8,
                borderBottom: "2px solid #e5e7eb",
                width: "fit-content"
              }}>
                <button
                  onClick={() => setContentTab("edit")}
                  style={{
                    background: contentTab === "edit" ? "#fff" : "transparent",
                    color: contentTab === "edit" ? "#2563eb" : "#374151",
                    border: "none",
                    borderBottom: contentTab === "edit" ? "3px solid #2563eb" : "3px solid transparent",
                    borderRadius: 0,
                    padding: "8px 20px",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: 14,
                    outline: "none",
                    transition: "background 0.15s, color 0.15s, border-bottom 0.15s"
                  }}
                >Edit</button>
                <button
                  onClick={() => setContentTab("raw")}
                  style={{
                    background: contentTab === "raw" ? "#fff" : "transparent",
                    color: contentTab === "raw" ? "#2563eb" : "#374151",
                    border: "none",
                    borderBottom: contentTab === "raw" ? "3px solid #2563eb" : "3px solid transparent",
                    borderRadius: 0,
                    padding: "8px 20px",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: 14,
                    outline: "none",
                    transition: "background 0.15s, color 0.15s, border-bottom 0.15s"
                  }}
                >Raw HTML</button>
              </div>
              {contentTab === "edit" ? (
                <div style={{ flex: 1, minHeight: 280, marginBottom: 16 }} className="quill-editor-wrapper">
                  <style>{`
                    .quill-editor-wrapper .ql-container {
                      min-height: 720px;
                    }
                    .quill-editor-wrapper .ql-editor {
                      min-height: 720px;
                      font-size: 15px;
                      line-height: 1.7;
                    }
                    .quill-editor-wrapper .ql-editor img {
                      max-width: 100%;
                      height: auto !important;
                      border-radius: 6px;
                      display: block;
                    }
                    .quill-editor-wrapper .ql-editor img[width] {
                      width: attr(width px);
                      max-width: 100%;
                    }
                    .quill-editor-wrapper .ql-editor h1,
                    .quill-editor-wrapper .ql-editor h2,
                    .quill-editor-wrapper .ql-editor h3 {
                      margin-top: 0.8em;
                      margin-bottom: 0.4em;
                    }
                    .quill-editor-wrapper .ql-editor p {
                      margin-bottom: 0.6em;
                    }
                    .quill-editor-wrapper .ql-snow .ql-editor img {
                      max-width: 100%;
                      height: auto !important;
                    }
                  `}</style>
                  <ReactQuill
                    theme="snow"
                    value={editForm.content}
                    onChange={val => setEditForm(f => ({ ...f, content: val }))}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ borderRadius: 6 }}
                  />
                </div>
              ) : (
                <textarea
                  value={editForm.content}
                  onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="Content"
                  style={{
                    flex: 1,
                    minHeight: 280,
                    padding: "12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 15,
                    resize: "vertical",
                    fontFamily: "monospace"
                  }}
                />
              )}
            </div>
            </div>
            {/* Progress bar */}
            {aiEditing && (
              <div style={{ padding: "0 28px", flexShrink: 0 }}>
                <div style={{
                  width: "100%",
                  height: 4,
                  background: "#e5e7eb",
                  borderRadius: 2,
                  overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%",
                    width: `${articleProgress}%`,
                    background: "linear-gradient(90deg, #2563eb, #3b82f6)",
                    borderRadius: 2,
                    transition: articleProgress === 100 ? "width 0.3s ease" : "width 0.4s linear",
                  }} />
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, textAlign: "center" }}>
                  {articleProgress < 95
                    ? "Applying your AI edit — this usually takes about a minute…"
                    : "Almost there…"}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", padding: "12px 28px 20px 28px", borderTop: "1px solid #e5e7eb", flexShrink: 0 }}>
              {!isMobile && (
              <button
                onClick={closeEditModal}
                disabled={savingEdit || aiEditing}
                style={{
                  background: "#f1f5f9",
                  color: "#374151",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15
                }}
              >
                Close
              </button>
              )}
              {editModal.slug && (
              <a
                href={(() => {
                  const article = articles.find(a => a.slug === editModal.slug);
                  const baseUrl = article?.article_url || `https://www.homeprice.site/news/${editModal.slug}${article?.landing_page_slug ? `?agent=${article.landing_page_slug}` : ""}`;
                  return baseUrl + (baseUrl.includes("?") ? "&" : "?") + "nopopup";
                })()}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: "#f1f5f9",
                  color: "#2563eb",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15,
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {!isMobile && <FaEye style={{ marginRight: 8 }} />} {isMobile ? "View" : "View Article"}
              </a>
              )}
              <button
                onClick={handleEditSubmit}
                disabled={savingEdit}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {savingEdit && <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />}
                {savingEdit ? "Saving..." : isMobile ? "Save" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Generate Article Modal */}
      {generateModal && (
        <div
          onClick={closeGenerateModal}
          style={{
            position: "fixed",
            top: 0, right: 0, bottom: 0,
            left: isMobile ? 0 : 260,
            background: "rgba(0,0,0,0.25)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 0,
              width: "100%",
              height: "100%",
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, padding: "20px 28px 12px 28px", borderBottom: "1px solid #e5e7eb", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              Generate New Article
              <button
                onClick={closeGenerateModal}
                disabled={generating || savingGenerate}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#9ca3af", padding: "0 4px", lineHeight: 1 }}
                title="Close"
              >×</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Title */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Title <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span></label>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Leave blank and the AI will generate a headline for you, or type your own to use as the article title.</div>
              <input
                value={generateForm.title}
                onChange={e => setGenerateForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Leave blank for AI-generated title…"
                style={{
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: "1px solid #e5e7eb",
                  fontSize: 16,
                  width: "100%",
                  boxSizing: "border-box"
                }}
              />
            </div>
            {/* Image URL */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Cover Image URL <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span></label>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Paste a URL to a cover image. This appears at the top of your article and as the thumbnail in listings.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {[
                  { label: "🏡 For Sale Sign", url: "https://images.pexels.com/photos/8292794/pexels-photo-8292794.jpeg" },
                  { label: "🏠 Modern House", url: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg" },
                  { label: "🏢 Apartments", url: "https://images.pexels.com/photos/144632/pexels-photo-144632.jpeg" },
                  { label: "🔑 Keys", url: "https://images.pexels.com/photos/8293778/pexels-photo-8293778.jpeg" },
                  { label: "🍳 Modern Interior", url: "https://images.pexels.com/photos/6284229/pexels-photo-6284229.jpeg" },
                ].map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setGenerateForm(f => ({ ...f, image_url: img.url }))}
                    disabled={generating || savingGenerate}
                    style={{
                      background: generateForm.image_url === img.url ? "#dbeafe" : "#f3f4f6",
                      color: generateForm.image_url === img.url ? "#1d4ed8" : "#374151",
                      border: generateForm.image_url === img.url ? "1px solid #93c5fd" : "1px solid #e5e7eb",
                      borderRadius: 999,
                      padding: "5px 14px",
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: (generating || savingGenerate) ? "not-allowed" : "pointer",
                      whiteSpace: "nowrap",
                      transition: "all 0.15s",
                    }}
                  >
                    {img.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
                <input
                  value={generateForm.image_url}
                  onChange={e => setGenerateForm(f => ({ ...f, image_url: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                  disabled={generating || savingGenerate || uploadingGenerateImage}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 16,
                    flex: 1,
                    minWidth: 0,
                    boxSizing: "border-box"
                  }}
                />
                <button
                  type="button"
                  onClick={() => generateUploadInputRef.current?.click()}
                  disabled={generating || savingGenerate || uploadingGenerateImage}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 6,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#374151",
                    cursor: (generating || savingGenerate || uploadingGenerateImage) ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {uploadingGenerateImage ? "Uploading…" : "Upload"}
                </button>
                <input
                  ref={generateUploadInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={e => {
                    if (e.target.files?.[0]) uploadCoverImage(e.target.files[0], "generate");
                    e.target.value = "";
                  }}
                />
              </div>
              {uploadImageError && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#dc2626" }}>
                  {uploadImageError}
                </div>
              )}
              {generateForm.image_url && (
                <img
                  src={generateForm.image_url}
                  alt="preview"
                  referrerPolicy="no-referrer"
                  style={{ marginTop: 8, maxHeight: 120, borderRadius: 6, objectFit: "cover" }}
                  onError={e => { e.target.style.display = "none"; }}
                  onLoad={e => { e.target.style.display = "block"; }}
                />
              )}
            </div>
            {/* Suburb / Postcode multi-select typeahead */}
            <div style={{ position: "relative" }}>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Suburbs / Postcodes</label>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Choose one or more suburbs or postcodes to pull market data for. Selecting a postcode includes all suburbs within it. Multiple selections will be combined in the article.</div>
              {selectedLocations.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {selectedLocations.map((loc, idx) => (
                    <span
                      key={`${loc.type}-${loc.value}-${idx}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        borderRadius: 16,
                        padding: "3px 10px",
                        fontSize: 13,
                        fontWeight: 600
                      }}
                    >
                      {formatLocationLabel(loc)}
                      <button
                        onClick={() => handleRemoveLocation(idx)}
                        disabled={generating || savingGenerate}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#1d4ed8",
                          cursor: "pointer",
                          padding: 0,
                          fontSize: 15,
                          lineHeight: 1,
                          fontWeight: 700,
                          marginLeft: 2
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ position: "relative" }}>
                <input
                  ref={suburbInputRef}
                  value={suburbQuery}
                  autoComplete="off"
                  name="suburb-search"
                  onChange={e => { setSuburbQuery(e.target.value); searchSuburbs(e.target.value); }}
                  onFocus={() => { if (suburbResults.length > 0) setSuburbDropdownOpen(true); }}
                  onBlur={() => { setTimeout(() => { setSuburbDropdownOpen(false); setSuburbQuery(""); }, 200); }}
                  placeholder="Type a suburb or postcode…"
                  disabled={generating || savingGenerate}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 15,
                    boxSizing: "border-box"
                  }}
                />
                {suburbSearching && (
                  <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
                    <Spinner animation="border" size="sm" style={{ width: 16, height: 16 }} />
                  </div>
                )}
              </div>
              {suburbDropdownOpen && suburbResults.length > 0 && (
                <div style={{
                  position: "absolute",
                  left: 0, right: 0,
                  top: "100%",
                  zIndex: 10,
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  boxShadow: "0 8px 24px #0002",
                  maxHeight: 220,
                  overflowY: "auto",
                  marginTop: 4
                }}>
                  {suburbResults.map((item, idx) => (
                    <div
                      key={`${item.type}-${item.value}-${idx}`}
                      onMouseDown={e => { e.preventDefault(); handleSelectLocation(item); }}
                      style={{
                        padding: "10px 14px",
                        cursor: "pointer",
                        fontSize: 14,
                        borderBottom: idx < suburbResults.length - 1 ? "1px solid #f3f4f6" : "none",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                      onMouseOver={e => e.currentTarget.style.background = "#f0f7ff"}
                      onMouseOut={e => e.currentTarget.style.background = "#fff"}
                    >
                      <span>{formatLocationLabel(item)}</span>
                      <span style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", fontWeight: 600 }}>{item.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Prompt - hidden after article is generated */}
            {!generateForm.slug && (
            <div>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Prompt</label>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Describe the article you'd like the AI to write. Be specific about the topic, location, and angle for best results.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {[
                  { label: "📊 Market Update", prompt: "Write a detailed market update for the selected suburbs covering recent property price trends, median prices, days on market, and rental data." },
                  { label: "🏡 Suburb Profile", prompt: "Write an engaging suburb profile article covering lifestyle, amenities, property market overview, demographics, and why it's a great place to live or invest." },
                  { label: "📈 Investment Guide", prompt: "Write a property investment guide for the area covering rental yields, capital growth, vacancy rates, and tips for investors." },
                  { label: "🏠 Buyer's Guide", prompt: "Write a first home buyer's guide for the area covering price ranges, popular property types, local amenities, and tips for getting into the market." },
                  { label: "🔥 Auction Recap", prompt: "Write an auction market recap for the area covering recent auction results, clearance rates, notable sales, and what it means for buyers and sellers." },
                ].map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setGenerateForm(f => ({ ...f, prompt: example.prompt }))}
                    disabled={generating}
                    style={{
                      background: generateForm.prompt === example.prompt ? "#dbeafe" : "#f3f4f6",
                      color: generateForm.prompt === example.prompt ? "#1d4ed8" : "#374151",
                      border: generateForm.prompt === example.prompt ? "1px solid #93c5fd" : "1px solid #e5e7eb",
                      borderRadius: 999,
                      padding: "5px 14px",
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: generating ? "not-allowed" : "pointer",
                      whiteSpace: "nowrap",
                      transition: "all 0.15s",
                    }}
                  >
                    {example.label}
                  </button>
                ))}
              </div>
              <textarea
                value={generateForm.prompt}
                onChange={e => setGenerateForm(f => ({ ...f, prompt: e.target.value }))}
                placeholder="e.g. Write a market update for Corrimal, NSW covering recent property price trends and rental data…"
                disabled={generating}
                rows={4}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 6,
                  border: "1px solid #e5e7eb",
                  fontSize: 15,
                  boxSizing: "border-box",
                  resize: "vertical",
                  fontFamily: "inherit"
                }}
              />
            </div>
            )}
            {/* AI Edit Prompt - only after article is generated */}
            {generateForm.slug && (
            <div>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>AI Edit Prompt</label>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Describe changes you'd like the AI to make to your article. You can also paste source text or data for the AI to reference.</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {[
                  { label: "✍️ More Professional", prompt: "Make the tone more professional and authoritative while keeping the content accessible." },
                  { label: "📝 Add CTA", prompt: "Add a strong call-to-action at the end encouraging readers to contact the agent for a free property appraisal." },
                  { label: "✂️ Make Shorter", prompt: "Condense the article to be more concise while keeping the key statistics and insights." },
                  { label: "🎯 SEO Optimise", prompt: "Optimise the article for SEO by improving headings, adding relevant keywords naturally, and ensuring good structure." },
                  { label: "💡 Add Statistics", prompt: "Enhance the article with more specific statistics, data points, and market comparisons." },
                ].map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setAiPrompt(example.prompt)}
                    disabled={aiEditing}
                    style={{
                      background: aiPrompt === example.prompt ? "#dbeafe" : "#f3f4f6",
                      color: aiPrompt === example.prompt ? "#1d4ed8" : "#374151",
                      border: aiPrompt === example.prompt ? "1px solid #93c5fd" : "1px solid #e5e7eb",
                      borderRadius: 999,
                      padding: "5px 14px",
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: aiEditing ? "not-allowed" : "pointer",
                      whiteSpace: "nowrap",
                      transition: "all 0.15s",
                    }}
                  >
                    {example.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexDirection: isMobile ? "column" : "row" }}>
                <textarea
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  placeholder={"Describe how to edit the article. You can also paste source text or data here for the AI to reference.\ne.g. Make the tone more professional, add a call-to-action…"}
                  disabled={aiEditing}
                  rows={3}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 15,
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "inherit",
                    width: isMobile ? "100%" : undefined
                  }}
                />
                <button
                  onClick={handleAiEdit}
                  disabled={aiEditing || !aiPrompt.trim()}
                  style={{
                    background: aiEditing ? "#a78bfa" : "#7c3aed",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "8px 18px",
                    fontWeight: 600,
                    cursor: aiEditing || !aiPrompt.trim() ? "not-allowed" : "pointer",
                    fontSize: 15,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                    minWidth: 100,
                    opacity: !aiPrompt.trim() ? 0.5 : 1,
                    width: isMobile ? "100%" : undefined
                  }}
                >
                  {aiEditing
                    ? <Spinner animation="border" size="sm" style={{ marginRight: 8, width: 14, height: 14 }} />
                    : <FaMagic style={{ marginRight: 8 }} />}
                  Apply
                </button>
              </div>
            </div>
            )}
            {/* Content editor - show during generation (streaming preview) or after article is generated */}
            {(generateForm.slug || generating) && (
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 2, display: "block" }}>Content</label>
              {generating ? (
                /* Streaming preview — outside the editor */
                <div style={{ flex: 1, minHeight: 280, marginBottom: 16 }}>
                  <div
                    ref={streamingArticleRef}
                    style={{
                      minHeight: 280,
                      maxHeight: 500,
                      overflowY: "auto",
                      padding: 16,
                      border: "1px solid #e5e7eb",
                      borderRadius: 6,
                      background: "#fff",
                    }}
                  />
                </div>
              ) : (
                /* Editor tabs — shown after generation is complete */
                <>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4, marginBottom: 8 }}>Use the rich-text editor to format your article, or switch to Raw HTML for direct code editing.</div>
              <div style={{
                display: "flex",
                gap: 0,
                marginBottom: 8,
                borderBottom: "2px solid #e5e7eb",
                width: "fit-content"
              }}>
                <button
                  onClick={() => setContentTab("edit")}
                  style={{
                    background: contentTab === "edit" ? "#fff" : "transparent",
                    color: contentTab === "edit" ? "#2563eb" : "#374151",
                    border: "none",
                    borderBottom: contentTab === "edit" ? "3px solid #2563eb" : "3px solid transparent",
                    borderRadius: 0,
                    padding: "8px 20px",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: 14,
                    outline: "none",
                    transition: "background 0.15s, color 0.15s, border-bottom 0.15s"
                  }}
                >Edit</button>
                <button
                  onClick={() => setContentTab("raw")}
                  style={{
                    background: contentTab === "raw" ? "#fff" : "transparent",
                    color: contentTab === "raw" ? "#2563eb" : "#374151",
                    border: "none",
                    borderBottom: contentTab === "raw" ? "3px solid #2563eb" : "3px solid transparent",
                    borderRadius: 0,
                    padding: "8px 20px",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: 14,
                    outline: "none",
                    transition: "background 0.15s, color 0.15s, border-bottom 0.15s"
                  }}
                >Raw HTML</button>
              </div>
              {contentTab === "edit" ? (
                <div style={{ flex: 1, minHeight: 280, marginBottom: 16 }} className="quill-editor-wrapper">
                    <ReactQuill
                      theme="snow"
                      value={generateForm.content}
                      onChange={val => setGenerateForm(f => ({ ...f, content: val }))}
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ borderRadius: 6 }}
                    />
                </div>
              ) : (
                <textarea
                  value={generateForm.content}
                  onChange={e => setGenerateForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="Content"
                  style={{
                    flex: 1,
                    minHeight: 280,
                    padding: "12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 15,
                    resize: "vertical",
                    fontFamily: "monospace"
                  }}
                />
              )}
                </>
              )}
            </div>
            )}
            </div>
            {/* Progress bar */}
            {(generating || aiEditing) && (
              <div style={{ padding: "0 28px", flexShrink: 0 }}>
                <div style={{
                  width: "100%",
                  height: 4,
                  background: "#e5e7eb",
                  borderRadius: 2,
                  overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%",
                    width: `${articleProgress}%`,
                    background: "linear-gradient(90deg, #2563eb, #3b82f6)",
                    borderRadius: 2,
                    transition: articleProgress === 100 ? "width 0.3s ease" : "width 0.4s linear",
                  }} />
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, textAlign: "center" }}>
                  {articleProgress < 95
                    ? (generating ? "Generating your article — this usually takes about a minute…" : "Applying your AI edit — this usually takes about a minute…")
                    : "Almost there…"}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", padding: "12px 28px 20px 28px", borderTop: "1px solid #e5e7eb", flexShrink: 0 }}>
              {!isMobile && (
              <button
                onClick={closeGenerateModal}
                disabled={generating || savingGenerate}
                style={{
                  background: "#f1f5f9",
                  color: "#374151",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15
                }}
              >
                Close
              </button>
              )}
              {!generateForm.slug && (
              <button
                onClick={handleGenerate}
                disabled={generating || !generateForm.prompt.trim()}
                style={{
                  background: generating ? "#93c5fd" : "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: generating || !generateForm.prompt.trim() ? "not-allowed" : "pointer",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  opacity: !generateForm.prompt.trim() ? 0.5 : 1
                }}
              >
                {generating && <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />}
                {generating ? "Generating…" : "Generate"}
              </button>
              )}
              {generateForm.slug && (
              <a
                href={(() => {
                  const article = articles.find(a => a.slug === generateForm.slug);
                  const baseUrl = article?.article_url || `https://www.homeprice.site/news/${generateForm.slug}${article?.landing_page_slug ? `?agent=${article.landing_page_slug}` : ""}`;
                  return baseUrl + (baseUrl.includes("?") ? "&" : "?") + "nopopup";
                })()}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: "#f1f5f9",
                  color: "#2563eb",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15,
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {!isMobile && <FaEye style={{ marginRight: 8 }} />} {isMobile ? "View" : "View Article"}
              </a>
              )}
              {generateForm.slug && (
              <button
                onClick={handleGenerateSave}
                disabled={savingGenerate}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {savingGenerate && <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />}
                {savingGenerate ? "Saving..." : isMobile ? "Save" : "Save Changes"}
              </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Social Post Modal */}
      {socialPostModal.open && (
        <div style={{
          position: "fixed",
          top: 0, right: 0, bottom: 0,
          left: isMobile ? 0 : 260,
          background: "rgba(0,0,0,0.25)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 0,
              width: "100%",
              height: "100%",
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, padding: "20px 28px 12px 28px", borderBottom: "1px solid #e5e7eb", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              Social Post
              <button
                onClick={() => setSocialPostModal({ open: false, slug: null })}
                disabled={socialPostLoading}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#9ca3af", padding: "0 4px", lineHeight: 1 }}
                title="Close"
              >×</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ fontWeight: 600, fontSize: 14, color: "#374151", marginBottom: 4, display: "block" }}>Prompt <span style={{ fontWeight: 400, color: "#9ca3af" }}>(edit to customise the blurb style)</span></label>
                <textarea
                  ref={socialPostPromptRef}
                  value={socialPostPrompt}
                  onChange={e => setSocialPostPrompt(e.target.value)}
                  disabled={socialPostLoading}
                  rows={2}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                    fontSize: 14,
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "inherit",
                    lineHeight: 1.5,
                    overflow: "hidden"
                  }}
                />
              </div>
              {socialPostLoading && !socialPostText ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, minHeight: 120, justifyContent: "center" }}>
                  <Spinner animation="border" />
                  <span style={{ color: "#6b7280", fontSize: 14 }}>Generating social post…</span>
                </div>
              ) : (
                <>
                  {!socialPostLoading && (
                  <p style={{ fontSize: 14, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>
                    Copy this post and share it on your social media — Facebook, Instagram, LinkedIn, or X (Twitter) — to drive traffic to your article and generate leads.
                  </p>
                  )}
                  <textarea
                    ref={socialPostTextRef}
                    value={socialPostText}
                    onChange={e => {
                      setSocialPostText(e.target.value);
                      e.target.style.height = "0px";
                      e.target.style.height = Math.max(200, e.target.scrollHeight) + "px";
                    }}
                    readOnly={socialPostLoading}
                    style={{
                      background: copyFlash ? "#dbeafe" : "#f3f4f6",
                      borderRadius: 8,
                      padding: "16px 18px",
                      fontSize: 15,
                      lineHeight: 1.7,
                      color: "#1f2937",
                      width: "100%",
                      boxSizing: "border-box",
                      border: copyFlash ? "1px solid #93c5fd" : "1px solid #e5e7eb",
                      transition: "background 0.3s ease, border 0.3s ease",
                      fontFamily: "inherit",
                      resize: "none",
                      overflow: "hidden",
                      minHeight: 200,
                      maxHeight: "none",
                    }}
                  />
                </>
              )}
            </div>
            {fbConnected && showScheduleFields && !socialPostLoading && socialPostText && (
              <div style={{ padding: "12px 28px 0", borderTop: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, color: "#555", display: "block", marginBottom: 6 }}>Date</label>
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={e => setScheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      style={{
                        width: "100%",
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        padding: "8px 12px",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <label style={{ fontWeight: 600, fontSize: 13, color: "#555", display: "block", marginBottom: 6 }}>Time</label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={e => setScheduleTime(e.target.value)}
                      style={{
                        width: "100%",
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        padding: "8px 12px",
                        fontSize: 14,
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <button
                      onClick={handleScheduleArticle}
                      disabled={scheduleSaving || !scheduleDate || !socialPostText.trim() || !fbSelectedPage}
                      style={{
                        background: (!scheduleDate || !socialPostText.trim()) ? "#d1d5db" : "#7c3aed",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        padding: "9px 20px",
                        fontWeight: 600,
                        cursor: (!scheduleDate || !socialPostText.trim()) ? "not-allowed" : "pointer",
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {scheduleSaving && <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />}
                      <FaCalendarAlt />
                      {scheduleSaving ? "Scheduling..." : "Schedule Post"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", padding: "12px 28px 20px 28px", borderTop: "1px solid #e5e7eb", flexShrink: 0, flexWrap: "wrap" }}>
              <button
                onClick={handleRegenerateBlurb}
                disabled={socialPostLoading}
                style={{
                  background: socialPostLoading ? "#a78bfa" : "#7c3aed",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: socialPostLoading ? "not-allowed" : "pointer",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: isMobile ? 1 : undefined
                }}
              >
                {socialPostLoading
                  ? <Spinner animation="border" size="sm" style={{ marginRight: 8, width: 14, height: 14 }} />
                  : <FaMagic style={{ marginRight: 8 }} />}
                Regenerate
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(socialPostText);
                  setCopyFlash(true);
                  setTimeout(() => setCopyFlash(false), 600);
                  setToast({ show: true, message: "Copied to clipboard!" });
                  setTimeout(() => setToast({ show: false, message: "" }), 3000);
                }}
                disabled={socialPostLoading || !socialPostText}
                style={{
                  background: "#2563eb",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: socialPostLoading || !socialPostText ? "not-allowed" : "pointer",
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: socialPostLoading || !socialPostText ? 0.5 : 1,
                  flex: isMobile ? 1 : undefined
                }}
              >
                <FaCopy style={{ marginRight: 8 }} /> Copy
              </button>
              {fbConnected && (() => {
                const article = socialPostArticleRef.current;
                const slug = article?.slug || "";
                const alreadyPosted = fbPosted.some(p => p.asset_type === "article" && p.asset_id === slug && p.page_id === fbSelectedPage);
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: isMobile ? 1 : undefined }}>
                    {fbPages.length > 1 && (
                      <select
                        value={fbSelectedPage}
                        onChange={e => { setFbSelectedPage(e.target.value); setFbPostMsg(""); }}
                        style={{
                          borderRadius: 6, border: "1px solid #d1d5db", padding: "7px 8px",
                          fontSize: 13, color: "#374151", background: "#f9fafb", maxWidth: 160,
                        }}
                      >
                        {fbPages.map(p => (
                          <option key={p.page_id} value={p.page_id}>{p.page_name}</option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={async () => {
                        setFbPosting(true);
                        setFbPostMsg("");
                        try {
                          const articleUrl = article?.article_url || (article?.slug
                            ? `https://www.homeprice.site/news/${article.slug}${article.landing_page_slug ? `?agent=${article.landing_page_slug}` : ""}`
                            : undefined);
                          const res = await handleTokenAndPostRequest(
                            `${window.api_host}/facebook-post`,
                            { message: socialPostText, link: articleUrl, page_id: fbSelectedPage, asset_type: "article", asset_id: slug },
                            { "Content-Type": "application/json" }
                          );
                          if (res.data.success) {
                            setFbPostMsg("success");
                            setFbPosted(prev => [...prev, { asset_type: "article", asset_id: slug, page_id: fbSelectedPage }]);
                            setToast({ show: true, message: "Posted to Facebook!" });
                            setTimeout(() => setToast({ show: false, message: "" }), 3000);
                          } else {
                            setFbPostMsg(res.data.message || "Failed to post.");
                            if (res.data.reconnect) setFbConnected(false);
                          }
                        } catch {
                          setFbPostMsg("Failed to post to Facebook.");
                        } finally {
                          setFbPosting(false);
                        }
                      }}
                      disabled={socialPostLoading || !socialPostText || fbPosting || alreadyPosted}
                      style={{
                        background: alreadyPosted ? "#9ca3af" : fbPostMsg === "success" ? "#059669" : "#1877f2",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 18px",
                        fontWeight: 600,
                        cursor: socialPostLoading || !socialPostText || fbPosting || alreadyPosted ? "not-allowed" : "pointer",
                        fontSize: 15,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: socialPostLoading || !socialPostText || fbPosting ? 0.7 : 1,
                        flex: isMobile ? 1 : undefined,
                        transition: "background 0.3s",
                      }}
                    >
                      {fbPosting
                        ? <Spinner animation="border" size="sm" style={{ marginRight: 8, width: 14, height: 14 }} />
                        : <FaFacebook style={{ marginRight: 8 }} />}
                      {alreadyPosted ? "Already Posted" : fbPostMsg === "success" ? "Posted!" : "Post to Facebook"}
                    </button>
                    <button
                      onClick={() => setShowScheduleFields(v => !v)}
                      disabled={socialPostLoading || !socialPostText}
                      style={{
                        background: showScheduleFields ? "#7c3aed" : "#f3f4f6",
                        color: showScheduleFields ? "#fff" : "#374151",
                        border: "none",
                        borderRadius: 6,
                        padding: "8px 14px",
                        fontWeight: 600,
                        cursor: socialPostLoading || !socialPostText ? "not-allowed" : "pointer",
                        fontSize: 15,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: socialPostLoading || !socialPostText ? 0.5 : 1,
                        transition: "background 0.3s, color 0.3s",
                      }}
                    >
                      <FaCalendarAlt style={{ marginRight: 6 }} /> Schedule
                    </button>
                  </div>
                );
              })()}
            </div>
            {fbPostMsg && fbPostMsg !== "success" && (
              <div style={{ padding: "0 28px 12px", fontSize: 13, color: "#dc2626" }}>{fbPostMsg}</div>
            )}
          </div>
        </div>
      )}
      {/* Toast notification */}
      {toast.show && (
        <div style={{
          position: "fixed",
          bottom: 24,
          left: isMobile ? "50%" : "calc(50% + 130px)",
          transform: "translateX(-50%)",
          background: "#065f46",
          color: "#fff",
          padding: "12px 24px",
          borderRadius: 8,
          fontSize: 15,
          fontWeight: 600,
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          zIndex: 2000,
          display: "flex",
          alignItems: "center",
          gap: 8,
          animation: "toast-fade-in 0.25s ease-out"
        }}>
          <span style={{ fontSize: 18 }}>✓</span> {toast.message}
        </div>
      )}
      <style>{`
        @keyframes toast-fade-in {
          from { opacity: 0; transform: translateX(-50%) translateY(12px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}